import { CheckIcon, CopyIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "../cn.js";

export interface CopyFieldProps {
	value: string;
	label?: string;
	/**
	 * DNS values and identifiers are monospaced so that similar glyphs can be
	 * told apart when retyping. Everything else uses the UI font (spec §28.2).
	 */
	monospace?: boolean;
	className?: string;
}

/**
 * A value the customer needs to transfer somewhere else, usually into their
 * DNS provider's control panel.
 */
export function CopyField({ value, label, monospace = true, className }: CopyFieldProps) {
	const [copied, setCopied] = useState(false);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

	const copy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(value);
		} catch {
			// Clipboard access can be denied. The value stays selectable, so
			// the customer can still copy it manually.
			return;
		}
		setCopied(true);
		if (timer.current) clearTimeout(timer.current);
		timer.current = setTimeout(() => setCopied(false), 2000);
	}, [value]);

	return (
		<div
			className={cn(
				"flex items-center gap-2 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-sunken) py-1.5 pl-3 pr-1.5",
				className,
			)}
		>
			<span
				className={cn("min-w-0 flex-1 select-all text-(--text-primary)", monospace ? "text-dns" : "text-sm")}
			>
				{value}
			</span>
			<button
				type="button"
				onClick={copy}
				aria-label={copied ? `${label ?? "Value"} copied` : `Copy ${label ?? "value"}`}
				className={cn(
					"inline-flex size-7 shrink-0 items-center justify-center rounded-(--radius-sm)",
					"text-(--text-muted) transition-colors hover:bg-(--surface) hover:text-(--text-primary)",
				)}
			>
				{copied ? (
					<CheckIcon className="size-4 text-(--color-positive-600)" weight="bold" aria-hidden="true" />
				) : (
					<CopyIcon className="size-4" aria-hidden="true" />
				)}
			</button>
			{/* Announced on copy without moving focus. */}
			<span role="status" aria-live="polite" className="sr-only">
				{copied ? "Copied to clipboard" : ""}
			</span>
		</div>
	);
}
